import APITemplate from '@api/APITemplate'
import con from '@services/connection'
import { intelligenceV2 } from '@modules/aawz-intelligence-v2/constants/URL'
import {
  ERRO_INESPERADO,
  requireId,
} from '@modules/aawz-intelligence-v2/api/guardas'

/**
 * As ENTREGAS de arquivo de uma conversa do Modo Agente (spec-008 p11).
 *
 * O agente produz planilha/apresentação/PDF dentro do run; sem estas duas
 * rotas o arquivo existe e **ninguém baixa** — `resource_interno_objetos.py`
 * é leitura INTERNA, com credencial do worker, e não pode ser exposta ao
 * navegador (é o motivo declarado do pedaço p11).
 *
 * Contrato (HTTP 200 sempre, status no corpo):
 *
 *   GET .../conversas/<id_conversa>/artefatos
 *     → `value.artefatos: [{id_objeto, nome, content_type, bytes, sha256,
 *        entregue_em, id_run}]`
 *   GET .../conversas/<id_conversa>/artefatos/<id_objeto>
 *     → `value: {base64, name, extension}` — o envelope base64 padrão da casa
 *     → `status: 403|404` com `value: null` quando o objeto não foi entregue
 *       nesta conversa, ou o profissional não a alcança
 *
 * ⚠️ 403/404 chegam com HTTP 200 e o status no CORPO: quem consome trata por
 * `r.status`, nunca por `catch` de HTTP.
 *
 * ★ Dedupe da LISTAGEM por conversa (`_listagemEmVoo`): toda bolha de
 * assistente com `id_run` pede a lista ao montar, e ao abrir uma conversa
 * elas montam no MESMO tick — sem o dedupe seriam N requisições idênticas
 * (a regra "Avoid N+1" do repo). Não há cache com validade: uma bolha que
 * NASCE depois (o turno que acabou de fechar) não acha requisição em voo e
 * faz a dela, que é justamente onde a entrega nova aparece. Memo com TTL
 * devolveria a lista de antes da entrega — verde na tela, arquivo invisível.
 */
class APIArtefatos extends APITemplate {
  constructor() {
    super()
    // `{ [id_conversa]: Promise }` — só o que está EM VOO agora. Estado de
    // instância (nunca escopo de módulo) pelo mesmo motivo do `apiChat`:
    // duas superfícies de chat abertas não compartilham buffer.
    this._listagemEmVoo = {}
  }

  /**
   * Lista as entregas da conversa.
   *
   * @param {String} id_conversa
   * @returns {Promise<Object>} envelope `{status, message, value}`.
   */
  async listar({ id_conversa } = {}) {
    const guard = requireId(id_conversa)
    if (guard) return guard

    // ⚠️ Nenhum `await` entre a checagem e a atribuição abaixo — um único
    // microtask cedido aqui reabre a corrida que o dedupe existe para fechar
    // (mesma armadilha documentada em `apiInsights.carregar`).
    const emVoo = this._listagemEmVoo[id_conversa]
    if (emVoo) return emVoo

    const promessa = this._buscarListagem(id_conversa)
    this._listagemEmVoo[id_conversa] = promessa
    try {
      return await promessa
    } finally {
      delete this._listagemEmVoo[id_conversa]
    }
  }

  async _buscarListagem(idConversa) {
    const r = await con.get(intelligenceV2.artefatosDaConversa(idConversa))
    return r || this.buildErrorResponse({ message: ERRO_INESPERADO })
  }

  /**
   * Baixa UMA entrega. Devolve o envelope cru — quem chama decide o que fazer
   * com `status` 403/404 (avisar) e com `value` (passar a `downloadBase64`).
   */
  async baixar({ id_conversa, id_objeto } = {}) {
    const guard = requireId(id_conversa) || requireId(id_objeto)
    if (guard) return guard

    const r = await con.get(
      intelligenceV2.artefatoDaConversa(id_conversa, id_objeto)
    )
    return r || this.buildErrorResponse({ message: ERRO_INESPERADO })
  }
}

const apiArtefatos = new APIArtefatos()

export { apiArtefatos }
